import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ShieldAlert, Info, X } from "lucide-react";
import { authHeaders } from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLE = {
  critical: {
    Icon: ShieldAlert,
    wrap: "bg-red-500/10 border-red-500/40 text-red-200",
    badge: "bg-red-500 text-black",
    label: "BLOCKING",
  },
  high: {
    Icon: AlertTriangle,
    wrap: "bg-amber-500/10 border-amber-500/40 text-amber-100",
    badge: "bg-amber-400 text-black",
    label: "ATTENTION",
  },
  info: {
    Icon: Info,
    wrap: "bg-zinc-900 border-white/10 text-zinc-300",
    badge: "bg-zinc-700 text-zinc-200",
    label: "NOTE",
  },
};

const LS_DISMISSED = "tok-admin-warning-dismissed-v1";

/**
 * Banner stack surfaced at the very top of the admin dashboard.
 * - Critical warnings (default passwords) cannot be dismissed.
 * - High / info warnings can be hidden for the current browser via a small X.
 */
export default function SecurityWarningBanner() {
  const [warnings, setWarnings] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(LS_DISMISSED) || "[]"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/security-warnings`, {
          headers: authHeaders(),
        });
        setWarnings(data.warnings || []);
      } catch {
        // Silently ignore — the banner is an enhancement, not blocking.
      }
    })();
  }, []);

  const dismiss = (key) => {
    const next = new Set(dismissed);
    next.add(key);
    setDismissed(next);
    localStorage.setItem(LS_DISMISSED, JSON.stringify([...next]));
  };

  const visible = warnings.filter(
    (w) => w.level === "critical" || !dismissed.has(w.key),
  );

  if (!visible.length) return null;

  return (
    <div data-testid="security-warning-banner" className="space-y-3 mb-6">
      {visible.map((w) => {
        const style = LEVEL_STYLE[w.level] || LEVEL_STYLE.info;
        const Icon = style.Icon;
        return (
          <div
            key={w.key}
            data-testid={`security-warning-${w.key}`}
            data-level={w.level}
            className={`border ${style.wrap} px-5 py-4 flex items-start gap-4`}
          >
            <Icon className="h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`text-[10px] uppercase tracking-[0.3em] font-black px-2 py-0.5 ${style.badge}`}
                >
                  {style.label}
                </span>
                <h3 className="font-display font-bold text-base">{w.title}</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed opacity-90">{w.detail}</p>
              {w.action && (
                <p className="mt-2 text-xs uppercase tracking-[0.2em] opacity-70">
                  → {w.action}
                </p>
              )}
            </div>
            {w.level !== "critical" && (
              <button
                type="button"
                onClick={() => dismiss(w.key)}
                data-testid={`security-warning-dismiss-${w.key}`}
                className="opacity-60 hover:opacity-100 transition-opacity"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
